import React, {useRef, useState} from "react";
import {
    Button,
    Form,
    FormGroup,
    FormLabel,
    FormSelect, ListGroup, ListGroupItem,
    Modal,
    ModalBody,
    ModalHeader
} from "react-bootstrap";
import useCallBackend from "../../hooks/use-call-backend";
import Error from "../../common/error";
import {VapSupplier} from "../../modal/vap-supplier"
import {VapCategory} from "../../modal/vap-category";
import {VapProduct} from "../../modal/vap-product";
import {VapProductOption} from "../../modal/vap-product-option";
import Loading from "../../common/loading";
import {ActivationRequestItem} from "../../modal/activation-request-item";

const AddVapOption:React.FC<{addItemHandler: (item:ActivationRequestItem) => void}> = (props) => {
    const {isLoading: loadingSuppliers, error: errorGettingSuppliers, invokeBackend: getSuppliers} = useCallBackend();
    const {isLoading: loadingCategories, error: errorGettingCategories, invokeBackend: getCategories} = useCallBackend();
    const {isLoading: loadingProducts, error: errorGettingProducts, invokeBackend: getProducts} = useCallBackend();
    const {isLoading: loadingOptions, error: errorGettingOptions, invokeBackend: getOptions} = useCallBackend();
    const [showAddModal, setShowAddModal] = useState(false);
    const [suppliers, setSuppliers] = useState<VapSupplier[]>([]);
    const [selectedSupplier, setSelectedSupplier] = useState("");
    const [categories, setCategories] = useState<VapCategory[]>([]);
    const [products, setProducts] = useState<VapProduct[]>([]);
    const [vapOptions, setVapOptions] = useState<VapProductOption[]>([]);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [isAnyOptionSelected, setIsAnyOptionSelected] = useState(false);
    const [errorAddingItem, setErrorAddingItem] = useState<string | null>(null);

    const categoryRef = useRef<HTMLSelectElement>(null);
    const productRef = useRef<HTMLSelectElement>(null);

    //Reset
    const handleHideModal = () => {
        if(loadingCategories || loadingProducts || loadingOptions) {
            alert("Please wait for operation to complete.");
            return;
        }
        setShowAddModal(false);
        setIsAllSelected(false);
        setIsAnyOptionSelected(false);
        setCategories([]);
        setProducts([]);
        setVapOptions([]);
    }

    const handleCategoryResponse = (categoryResponse: VapCategory[]) => {
        setCategories(categoryResponse);
    }

    const handleSupplierResponse = (supplierResponse: VapSupplier[]) => {
        if(supplierResponse.length > 0) {
            setSuppliers(supplierResponse);
            const firstOption = "" + supplierResponse[0].id;
            setSelectedSupplier(firstOption);
            getCategories({url: "/api/products/categories", headers: [{name: "Institution", value: firstOption}]}, handleCategoryResponse);
        }
    }

    const handleChangeSupplier = (event:React.ChangeEvent<HTMLSelectElement>) => {
        const supplierId = event.target.value;
        setSelectedSupplier(supplierId);
        setProducts([]);
        setVapOptions([]);
        setIsAllSelected(false);
        setIsAnyOptionSelected(false);

        getCategories({url: "/api/products/categories", headers: [{name: "Institution", value: supplierId}]}, handleCategoryResponse);
    }

    const handleProductsResponse = (products: VapProduct[]) => {
        setProducts(products);
    }

    const handleChangeCategory = (event:React.ChangeEvent<HTMLSelectElement>) => {
        const categoryId = event.target.value;
        setProducts([]);
        setVapOptions([]);
        setIsAllSelected(false);
        setIsAnyOptionSelected(false);

        if(categoryId !== ""){
            getProducts({url:"/api/products/" + categoryId, headers: [{name: "Institution", value: selectedSupplier}]}, handleProductsResponse);
        }
    }

    const handleOptionsResponse = (vapOptionList: VapProductOption[]) => {
        const newVapOptions: VapProductOption[] = vapOptionList.map(vapOption => {
           vapOption.selected = false;
           return vapOption
        });
        setVapOptions(newVapOptions);
    }

    const handleChangeProduct = (event:React.ChangeEvent<HTMLSelectElement>) => {
        const productId = event.target.value;
        setVapOptions([]);
        setIsAllSelected(false);
        setIsAnyOptionSelected(false);

        if(productId !== ""){
            getOptions({url:"/api/products/options/" + productId, headers: [{name: "Institution", value: selectedSupplier}]}, handleOptionsResponse);
        }
    }

    const handleSelectAll = () => {
        setIsAllSelected(!isAllSelected);
        setIsAnyOptionSelected(!isAllSelected);
        const newVapOptions: VapProductOption[] = vapOptions.map(vapOption => {
            vapOption.selected = !isAllSelected;
            return vapOption
        });
        setVapOptions(newVapOptions);
    }

    const handleSelectOption = (event: React.ChangeEvent<HTMLInputElement>) => {
        const item = event.target.value;

        const newVapOptions: VapProductOption[] = vapOptions.map(vapOption => {
            if(item === ''+vapOption.id){
                vapOption.selected = !vapOption.selected;
            }
            return vapOption
        });
        const isAllOptionsSelected = newVapOptions.every(element => element.selected);
        const checkIsAllUnselected = newVapOptions.every(element => !element.selected);
        setVapOptions(newVapOptions);
        setIsAllSelected(isAllOptionsSelected);
        setIsAnyOptionSelected(!checkIsAllUnselected);
    }

    const handleAddSeleted = () => {
        setErrorAddingItem(null) //reset;

        if(categoryRef == null || categoryRef.current == null) {
            setErrorAddingItem("Category not selected.")
            return;
        }

        if(productRef == null || productRef.current == null) {
            setErrorAddingItem("Product not selected.")
            return;
        }

        let selectedOptions: VapProductOption[] = [];

        if(!isAnyOptionSelected){
            setErrorAddingItem("No Options selected.")
            return;
        }else{
            selectedOptions = vapOptions.filter(option => option.selected);
        }

        const category = categoryRef.current;
        const product = productRef.current;

        const newItem: ActivationRequestItem = {
            activateAll: isAllSelected,
            category: {
                id: Number(category.value),
                name: category.options[category.selectedIndex].text
            },
            product: {
                id: Number(product.value),
                name: product.options[product.selectedIndex].text
            },
            options: selectedOptions
        };

        props.addItemHandler(newItem);
        handleHideModal();
    };

    const handleShowModal = () => {
        getSuppliers({url: "/api/institution/list"}, handleSupplierResponse);
        setShowAddModal(true);
    }

    const addButton = (
        <FormGroup className="mt-3">
            <Button variant="primary" onClick={handleAddSeleted}>Add</Button>
        </FormGroup>
    )

    const vapSuppliers = (
        <FormGroup>
            <FormLabel>Supplier:</FormLabel>
            <FormSelect onChange={handleChangeSupplier} disabled={loadingCategories || loadingProducts || loadingOptions} value={selectedSupplier}>
                {suppliers.map(supplier => (<option key={supplier.id} value={supplier.id}>{supplier.name}</option>))}
            </FormSelect>
        </FormGroup>
    )

    const vapCategories = (
        <FormGroup className="mt-3">
            <FormLabel>Category:</FormLabel>
            <FormSelect onChange={handleChangeCategory} disabled={loadingProducts || loadingOptions} ref={categoryRef}>
                <option value="">--Select Category--</option>
                {categories.map(category => (<option key={category.id} value={category.id}>{category.name}</option>))}
            </FormSelect>
        </FormGroup>
    )

    const allOption = (
        <ListGroupItem>
            <input type="checkbox" value="all" id="chkAll" className="me-1" checked={isAllSelected} onChange={handleSelectAll} />
            <label htmlFor="chkAll">All Options</label>
        </ListGroupItem>
    );

    const vapOptionsView = (
        <FormGroup className="mt-3">
            <FormLabel>Options:</FormLabel>
            <ListGroup>
                {vapOptions.length > 1 && allOption}
                {vapOptions.map(vapOption => (
                    <ListGroupItem key={vapOption.id}>
                        <input type="checkbox" className="me-1"
                               value={vapOption.id}
                               id={"option_" + vapOption.id}
                               onChange={handleSelectOption}
                               checked={vapOption.selected} />
                        <label htmlFor={"option_" + vapOption.id}>{vapOption.name}</label>
                    </ListGroupItem>
                ))}
            </ListGroup>
        </FormGroup>
    );

    const vapProducts = (
        <FormGroup className="mt-3">
            <FormLabel>Product:</FormLabel>
            <FormSelect onChange={handleChangeProduct} disabled={loadingOptions} ref={productRef}>
                <option>--Select Product--</option>
                {products.map(product => (<option key={product.id} value={product.id}>{product.name}</option>))}
            </FormSelect>
        </FormGroup>
    );

    const addVapContent = (
        <Form>
            {loadingSuppliers && <Loading />}
            {errorGettingSuppliers && <Error message={errorGettingSuppliers} />}
            {suppliers.length > 0 && vapSuppliers}
            {loadingCategories && <Loading />}
            {errorGettingCategories && <Error message={errorGettingCategories} />}
            {categories.length > 0 && vapCategories}
            {loadingProducts && <Loading />}
            {errorGettingProducts && <Error message={errorGettingProducts} />}
            {products.length > 0 && vapProducts}
            {loadingOptions && <Loading />}
            {errorGettingOptions && <Error message={errorGettingOptions} />}
            {vapOptions.length > 0 && vapOptionsView}
            {isAnyOptionSelected && addButton}
        </Form>
    );

    return (
        <React.Fragment>
            <Button variant="primary" onClick={handleShowModal} className="me-1">Add</Button>
            <Modal size="lg" show={showAddModal} onHide={handleHideModal} backdrop="static" animation={false}>
                <ModalHeader className="bg-light" closeButton>Add VAP to Request:</ModalHeader>
                <ModalBody>
                    {loadingCategories && <Loading />}
                    {errorGettingCategories && <Error message={errorGettingCategories} />}
                    {!loadingCategories && !errorGettingCategories && addVapContent}
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
}

export default AddVapOption;